import { useState, useEffect } from 'react';
import { useAuth } from 'context/AuthContext';
import api from 'services/api';

/* ═══════════════════════════════════════════════════════════════════════════
 * ÉDITION DES ÉTATS DU PERSONNEL — DEUX BOUTONS, JAMAIS UNE CASE À COCHER
 * ═══════════════════════════════════════════════════════════════════════════
 * ⚠️ DEUX BOUTONS DISTINCTS, DEUX PERMISSIONS DISTINCTES (Dom, 03/09/2026) :
 *
 *   « État du personnel »          → admin · dg · rh
 *      Nominatif, AUCUN montant. C'est celui qui SORT de l'entreprise :
 *      appel d'offres, client qui veut savoir qui garde son site.
 *
 *   « État de la masse salariale » → dg · comptable SEULS
 *      Avec les rémunérations. Strictement INTERNE, ne se remet à aucun client.
 *
 *   ➡️ JAMAIS une case « masquer les salaires » : une case se décoche par
 *      erreur, deux boutons ne se confondent pas.
 *
 * ⚠️ LE SECOND BOUTON N'EST PAS AFFICHÉ GRISÉ — IL N'EST PAS AFFICHÉ DU TOUT.
 *    Un bouton grisé annonce à un utilisateur RH qu'un état des salaires
 *    existe et qu'on le lui refuse. Ce qu'il n'a pas le droit d'éditer, il n'a
 *    pas besoin de savoir que ça existe.
 *    ⓘ Le masquage est un confort d'écran : l'autorité reste la route
 *      (`authorize('dg','comptable')`), qui rend 403 quoi qu'il arrive.
 */

// ⚠️ MIROIR EXACT de backend/src/routes/etatPersonnelRoutes.js. Toute
//    divergence se voit : l'écran offrirait un bouton que la route refuse.
const PEUT_ETAT_PERSONNEL = ['admin', 'dg', 'rh'];
const PEUT_MASSE_SALARIALE = ['dg', 'comptable'];

/* ⚠️ LE LIBELLÉ DIT « DERNIER SITE PLANIFIÉ », PAS « SITE » (Dom, 04/09/2026).
 *    La colonne du document s'appelle « Dernier site planifié » parce que c'est
 *    tout ce que le modèle sait dire : il n'existe AUCUNE notion d'affectation
 *    d'un agent à un site (`agents` n'a pas de colonne de site ; le seul chemin
 *    est le dernier `planning`). Écrire « site » ici ferait promettre au libellé
 *    ce que le document ne contient pas.
 *    ➡️ Quand l'affectation datée existera (ops/chantier-affectation-datee.md),
 *       la colonne deviendra « Site d'affectation » ET LE LIBELLÉ SUIVRA.
 *       Pas avant : le libellé suit la colonne, jamais l'inverse.
 */

/* ⚠️ LE SOUS-MENU RESTE **UNE** ENTRÉE (Dom, 03/09/2026).
 *    On ne découpe pas « États du personnel » par population : le menu porte le
 *    DOCUMENT, l'écran porte le FILTRE. Découper par population ferait perdre
 *    l'état « tout le personnel » — celui qu'un donneur d'ordre demande.
 *    Le filtre n'était pas assez visible : il passe donc EN HAUT, avant les
 *    boutons, en trois pastilles larges — pas une liste déroulante, qu'on
 *    n'ouvre pas et dont on ne lit pas la valeur.
 */
const POPULATIONS = [
  { code: 'agents', label: 'Agents', cle: 'agents' },
  { code: 'admin', label: 'Personnel administratif', cle: 'admin' },
  { code: 'tous', label: 'Tout le personnel', cle: 'tous' },
];

// ⚠️ L'UNION DES STATUTS DES DEUX POPULATIONS. Choisir « en congé » ne remonte
//    que des agents — ce n'est pas un défaut, c'est la réalité : le personnel
//    administratif n'a pas cet état aujourd'hui. On n'invente pas un
//    `en_conge` administratif pour faire joli.
const STATUTS = [
  { code: '', label: 'Tous les statuts' },
  { code: 'actif', label: 'Actif' },
  { code: 'suspendu', label: 'Suspendu (agents)' },
  { code: 'inactif', label: 'Inactif' },
  { code: 'en_conge', label: 'En congé (agents)' },
];

const CONTRATS = ['', 'CDI', 'CDD', 'Periode_essai', 'Interim', 'Stage'];

export default function EtatsPersonnelPage() {
  const { user } = useAuth();
  const roles = (user && user.roles) || [];
  const a = (liste) => liste.some((r) => roles.includes(r));

  // ⚠️ PAR DÉFAUT : TOUT LE PERSONNEL. C'est l'état complet qu'on demande le
  //    plus souvent ; un défaut restrictif produirait des états partiels sans
  //    que personne ne s'en aperçoive.
  const [population, setPopulation] = useState('tous');
  const [statut, setStatut] = useState('');
  const [typeContrat, setTypeContrat] = useState('');
  const [enCours, setEnCours] = useState(null);
  const [erreur, setErreur] = useState('');

  // ── L'EFFECTIF, LU EN DIRECT ────────────────────────────────────────────────
  // ⚠️ AUCUNE VALEUR EN DUR, AUCUN REPLI À ZÉRO. Tant que le comptage n'est pas
  //    revenu, on affiche « … » ; s'il échoue, on affiche « ? » et on le dit.
  //    Un « (0) » inventé ferait croire à un effectif vide.
  const [effectifs, setEffectifs] = useState(null);
  const [effectifsErreur, setEffectifsErreur] = useState('');

  useEffect(() => {
    let vivant = true;
    api.get('/etats/effectifs')
      .then((r) => { if (vivant) setEffectifs(r.data); })
      .catch(() => { if (vivant) setEffectifsErreur("L'effectif n'a pas pu être lu."); });
    return () => { vivant = false; };
  }, []);

  const compte = (cle) => {
    if (effectifsErreur) return '?';
    if (!effectifs) return '…';
    return effectifs[cle];
  };

  const editer = async (chemin, format) => {
    setErreur(''); setEnCours(`${chemin}-${format}`);
    try {
      const params = new URLSearchParams({ population, format });
      if (statut) params.set('statut', statut);
      if (typeContrat) params.set('type_contrat', typeContrat);
      // ⚠️ `responseType: 'blob'` : la réponse est un DOCUMENT, pas du JSON.
      const r = await api.get(`/etats/${chemin}?${params}`, { responseType: 'blob' });
      // Le nom du fichier vient du SERVEUR (Content-Disposition) : c'est lui
      // qui connaît le code court du tenant et la date d'édition.
      const cd = r.headers['content-disposition'] || '';
      const m = /filename="?([^"]+)"?/.exec(cd);
      const nom = m ? m[1] : `etat.${format === 'excel' ? 'xlsx' : 'pdf'}`;
      const url = URL.createObjectURL(r.data);
      const lien = document.createElement('a');
      lien.href = url; lien.download = nom;
      document.body.appendChild(lien); lien.click(); lien.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      // ⚠️ ON DIT L'ÉCHEC. Une erreur avalée ici, c'est l'utilisateur qui clique
      //    trois fois en croyant que rien ne part.
      // ⚠️ ON DIT L'ÉCHEC, ON N'EXPLIQUE PAS LA RESTRICTION. Le produit n'explique
      //    pas ses restrictions à l'écran : un bouton non proposé n'a pas à être
      //    justifié. Ce message ne se voit que si un appel échoue pour une autre
      //    raison — les boutons affichés sont ceux que la route accepte.
      let msg = 'La génération a échoué.';
      if (e.response?.data instanceof Blob) {
        try { msg = JSON.parse(await e.response.data.text()).error || msg; } catch { /* corps illisible */ }
      }
      setErreur(msg);
    } finally { setEnCours(null); }
  };

  const S = {
    carte: { background: 'var(--gss-surface)', border: '1px solid var(--gss-border)', borderRadius: 10, padding: 18, marginBottom: 16 },
    label: { display: 'block', fontSize: 12, color: 'var(--gss-text-light)', marginBottom: 5 },
    input: { width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid var(--gss-border)', background: 'var(--gss-bg)', color: 'var(--gss-text)', fontSize: 13 },
    bloc: { border: '1px solid var(--gss-border)', borderRadius: 10, padding: 16, flex: '1 1 320px' },
    btn: (actif) => ({ padding: '9px 16px', borderRadius: 7, border: 'none', fontWeight: 700, fontSize: 13,
      cursor: actif ? 'pointer' : 'not-allowed', background: actif ? 'var(--gss-red)' : 'var(--gss-border)',
      color: actif ? '#FFF' : 'var(--gss-text-light)', marginRight: 8 }),
    // Pastille de population. L'option active est ÉVIDENTE : fond plein, texte
    // inversé, liseré. L'inactive reste lisible, jamais grisée à l'illisible.
    pastille: (actif) => ({
      flex: '1 1 220px', minWidth: 180, textAlign: 'left', cursor: 'pointer',
      padding: '12px 16px', borderRadius: 10, fontFamily: 'inherit',
      border: actif ? '2px solid var(--gss-red)' : '1px solid var(--gss-border)',
      background: actif ? 'var(--gss-red)' : 'var(--gss-bg)',
      color: actif ? '#FFF' : 'var(--gss-text)',
      boxShadow: actif ? '0 2px 10px rgba(0,0,0,0.18)' : 'none',
      transition: 'background .12s, color .12s',
    }),
    pastilleLabel: { display: 'block', fontSize: 13, fontWeight: 700, lineHeight: 1.25 },
    pastilleCompte: (actif) => ({ display: 'block', marginTop: 4, fontSize: 20, fontWeight: 800, lineHeight: 1,
      color: actif ? '#FFF' : 'var(--gss-red)' }),
  };

  return (
    <div style={{ padding: 20 }}>
      <h1 style={{ margin: '0 0 4px', fontSize: 20 }}>États du personnel</h1>
      <p style={{ margin: '0 0 16px', color: 'var(--gss-text-light)', fontSize: 13 }}>
        L'effectif est constaté <strong>à l'instant de l'édition</strong> : le statut du personnel n'étant pas
        historisé, ces documents ne peuvent pas restituer l'effectif à une date passée. La date et l'heure du
        constat sont écrites sur le document.
      </p>

      {/* ── ① LA POPULATION — EN HAUT, AVANT LES BOUTONS ────────────────────
          Trois pastilles larges, pas une liste déroulante. L'effectif est lu en
          direct et s'affiche à côté de chaque choix. Le choix est écrit sur le
          document produit. */}
      <div style={S.carte}>
        <label style={{ ...S.label, fontSize: 13, fontWeight: 700, color: 'var(--gss-text)', marginBottom: 10 }}>
          Population de l'état
        </label>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }} role="group" aria-label="Population de l'état">
          {POPULATIONS.map((p) => {
            const actif = population === p.code;
            return (
              <button key={p.code} type="button" aria-pressed={actif}
                style={S.pastille(actif)} onClick={() => setPopulation(p.code)}>
                <span style={S.pastilleLabel}>{p.label}</span>
                <span style={S.pastilleCompte(actif)}>{compte(p.cle)}</span>
              </button>
            );
          })}
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 11, color: effectifsErreur ? 'var(--gss-danger)' : 'var(--gss-text-light)' }}>
          {effectifsErreur
            ? `${effectifsErreur} Les effectifs affichés ne sont pas fiables ; l'édition, elle, compte à l'instant du tirage.`
            : (effectifs ? effectifs.perimetre : 'Lecture de l’effectif…')}
        </p>
      </div>

      {/* ── ② LES AUTRES FILTRES — tout filtre appliqué est ÉCRIT sur le document */}
      <div style={S.carte}>
        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 200px' }}>
            <label style={S.label}>Statut</label>
            <select style={S.input} value={statut} onChange={(e) => setStatut(e.target.value)}>
              {STATUTS.map((s) => <option key={s.code} value={s.code}>{s.label}</option>)}
            </select>
          </div>
          <div style={{ flex: '1 1 200px' }}>
            <label style={S.label}>Type de contrat <span style={{ fontSize: 10 }}>(agents seulement)</span></label>
            <select style={S.input} value={typeContrat} onChange={(e) => setTypeContrat(e.target.value)}>
              {CONTRATS.map((c) => <option key={c} value={c}>{c || 'Tous les contrats'}</option>)}
            </select>
          </div>
        </div>
        <p style={{ margin: '10px 0 0', fontSize: 11, color: 'var(--gss-text-light)' }}>
          Les filtres choisis sont <strong>écrits sur le document</strong> : un état filtré qui ne dit pas
          comment il a été filtré est un état trompeur.
        </p>
      </div>

      {erreur && (
        <div style={{ background: 'rgba(239,68,68,0.10)', border: '1px solid var(--gss-danger)', borderRadius: 8,
          padding: '10px 12px', marginBottom: 14, fontSize: 13, color: 'var(--gss-danger)' }}>{erreur}</div>
      )}

      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        {/* ── BOUTON 1 — l'état qui SORT de l'entreprise ──────────────────── */}
        {a(PEUT_ETAT_PERSONNEL) && (
          <div style={S.bloc}>
            <h2 style={{ margin: '0 0 6px', fontSize: 15 }}>État du personnel</h2>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--gss-text-light)' }}>
              L'effectif de la société à la date d'édition : nom, matricule, poste, statut,
              dernier site planifié. La pièce qui sort de l'entreprise — appel d'offres, client
              qui veut savoir qui garde son site, contrôle d'effectif.
            </p>
            <button style={S.btn(enCours !== 'personnel-pdf')} disabled={!!enCours}
              onClick={() => editer('personnel', 'pdf')}>
              {enCours === 'personnel-pdf' ? 'Génération…' : 'Générer le PDF'}
            </button>
            <button style={{ ...S.btn(enCours !== 'personnel-excel'), background: 'transparent',
              border: '1px solid var(--gss-border)', color: 'var(--gss-text)' }} disabled={!!enCours}
              onClick={() => editer('personnel', 'excel')}>
              {enCours === 'personnel-excel' ? 'Génération…' : "Générer l'Excel"}
            </button>
          </div>
        )}

        {/* ── BOUTON 2 — INTERNE. Absent, pas grisé, si la permission manque ── */}
        {a(PEUT_MASSE_SALARIALE) && (
          <div style={{ ...S.bloc, borderColor: 'var(--gss-danger)' }}>
            <h2 style={{ margin: '0 0 6px', fontSize: 15 }}>État de la masse salariale</h2>
            <p style={{ margin: '0 0 14px', fontSize: 12, color: 'var(--gss-danger)' }}>
              Les rémunérations par personne et les totaux par catégorie. Document interne.
            </p>
            <button style={S.btn(enCours !== 'masse-salariale-pdf')} disabled={!!enCours}
              onClick={() => editer('masse-salariale', 'pdf')}>
              {enCours === 'masse-salariale-pdf' ? 'Génération…' : 'Générer le PDF'}
            </button>
            <button style={{ ...S.btn(enCours !== 'masse-salariale-excel'), background: 'transparent',
              border: '1px solid var(--gss-border)', color: 'var(--gss-text)' }} disabled={!!enCours}
              onClick={() => editer('masse-salariale', 'excel')}>
              {enCours === 'masse-salariale-excel' ? 'Génération…' : "Générer l'Excel"}
            </button>
          </div>
        )}

        {/* ⚠️ RETIRÉ LE 03/09/2026 — « Votre profil n'autorise l'édition d'aucun
            état du personnel. » Le produit n'explique pas ses restrictions à
            l'écran : ce qu'un rôle ne peut pas faire ne lui est ni montré ni
            expliqué. Ce bloc était de toute façon inatteignable — l'entrée de
            menu et la route sont gardées en amont (permissions.js:43 et 71). */}
      </div>

      <p style={{ marginTop: 16, fontSize: 11, color: 'var(--gss-text-light)' }}>
        Chaque édition est <strong>journalisée</strong> : qui, quand, quel état, quels filtres, combien de
        personnes. Le comptage <strong>exclut les fiches supprimées</strong> et le document dit ce qu'il compte.
      </p>
    </div>
  );
}
